import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { RegisterTerminalModalJustifiSteps, useRegisterTerminalModal } from '../hooks';
import {
  RegisterTerminalModalStepProps,
  RegisterTerminalStepModalWrapper,
  RegisterTerminalStep,
} from '../register-terminal-modal-step';

const styles = {
  list: css`
    list-style-type: number;
    padding-left: ${theme.spacing(2)};
    margin-top: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(1)};
    & > li:not(:first-of-type) {
      margin-top: ${theme.spacing(2)};
    }
    & > li:not(:last-of-type) {
      margin-bottom: ${theme.spacing(2)};
    }
  `,
  italicText: css`
    color: ${theme.font.colors.light};
  `,
};

export const ConnectToWifiStep1 = (props: RegisterTerminalModalStepProps) => {
  const { t } = useTranslation('payments');
  return (
    <RegisterTerminalStepModalWrapper
      title={t('Connect to WiFi')}
      nextStep={RegisterTerminalModalJustifiSteps.ConnectToWifiStep2}
      Action={RegisterTerminalStep.Next}
      {...props}
    >
      <ol css={styles.list}>
        <li>{t('After the boot-up is complete, the device will display the blank "Device ID" screen')}</li>
        <li>{t('Press 1+5+9 simultaneously on the keypad. This will display the "SCA Desktop" menu')}</li>
        <li>{t('Select "Com Control"')}</li>
        <li>{t('Select "WiFi". Enter 200331 and press "OK"')}</li>
      </ol>
    </RegisterTerminalStepModalWrapper>
  );
};

export const ConnectToWifiStep2 = (props: RegisterTerminalModalStepProps) => {
  const { t } = useTranslation('payments');
  return (
    <RegisterTerminalStepModalWrapper
      title={t('Connect to WiFi')}
      nextStep={RegisterTerminalModalJustifiSteps.ConnectToWifiStep3}
      Action={RegisterTerminalStep.Next}
      {...props}
    >
      <ol css={styles.list} start={5}>
        <li>{t('Press "WiFi Scan" to search for WiFi networks. Find your WiFi network, then press "ADD"')}</li>
        <li>
          {t(
            'Select "Pre Shared Key", and enter your WiFi password. Special keys are under the "#" button. Once entered press "OK"'
          )}
        </li>
        <li>{t('On the "Authentication" screen, select "SAVE"')}</li>
      </ol>
    </RegisterTerminalStepModalWrapper>
  );
};

export const ConnectToWifiStep3 = (props: RegisterTerminalModalStepProps) => {
  const { t } = useTranslation('payments');
  const { goToStep: goToStepDefault } = useRegisterTerminalModal();
  const goToStep = useMemo(() => props?.goToStep ?? goToStepDefault, [goToStepDefault, props?.goToStep]);

  return (
    <RegisterTerminalStepModalWrapper
      title={t('Connect to WiFi')}
      nextStep={RegisterTerminalModalJustifiSteps.GenrateDeviceId}
      Action={RegisterTerminalStep.Next}
      {...props}
    >
      <ol css={styles.list} start={8}>
        <li>{t('Select "OK" when prompted')}</li>
        <li>{t('Choose "Yes" when asked to apply setting on the interface')}</li>
        <li>
          {t('After WiFi connection is established, it will show "Accepted" and "Interface started". Select "OK"')}
        </li>
        <li>
          <Trans t={t}>
            <Text>
              On the WiFi scan menu, select the red 'x' on the terminal keypad{' '}
              <Text as='span' weight='bold'>
                5 times
              </Text>
              , ensuring the Verifone "Device ID" screen appears
            </Text>
          </Trans>
        </li>
      </ol>

      <Button
        variant='tertiary'
        onClick={() => goToStep(RegisterTerminalModalJustifiSteps.IncorrectPassword)}
        css={css`
          width: fit-content;
          font-weight: ${theme.font.weight.bold};
        `}
      >
        {t('Click here if you entered your password incorrectly.')}
      </Button>
    </RegisterTerminalStepModalWrapper>
  );
};

export const IncorrectPassword = (props: RegisterTerminalModalStepProps) => {
  const { t } = useTranslation('payments');
  return (
    <RegisterTerminalStepModalWrapper
      title={t('Incorrect WiFi Password Error')}
      nextStep={RegisterTerminalModalJustifiSteps.GenrateDeviceId}
      Action={RegisterTerminalStep.Next}
      {...props}
    >
      <ol css={styles.list}>
        <li>{t('Press the back button to get back to configuration')}</li>
        <li>{t('Press on your chosen WiFi network')}</li>
        <li>{t('Press "View/Edit"')}</li>
        <li>{t('Scroll to "Authentication" and press')}</li>
        <li>{t('Scroll to "Pre-Shared Key" and press')}</li>
        <li>{t('Delete and then re-enter your password correctly and press "OK"')}</li>
        <li>{t('Press the back arrow to go back to the View/Edit screen. Then select "Save"')}</li>
        <li>{t('Select "OK" when prompted')}</li>
        <li>{t('Choose "Yes" when asked to apply setting on the interface')}</li>
        <li>{t('After WiFi connection is establishes, it will show Accepted and Interface started. Select "OK"')}</li>
        <li>
          <Trans t={t}>
            <Text>
              On the configuration screen, select the red 'x' on the terminal keypad{' '}
              <Text as='span' weight='bold'>
                5 times
              </Text>
              , ensuring the Verifone device ID screen appears
            </Text>
          </Trans>
        </li>
      </ol>
    </RegisterTerminalStepModalWrapper>
  );
};
